// Canonical evidence checklist per verification-sensitive category.
//
// The AI returns free-text `evidence` (what it could see) and `prompts`
// (what it still needs). To turn that into a structured checklist UI, we
// define a fixed set of "ideal shots" per category and fuzzy-match each
// shot's keywords against both lists.
//
// Status:
//   - "provided": at least one keyword appears in AI's `evidence`
//   - "missing":  AI explicitly asked for it via `prompts`
//   - "optional": neither — would help but Fig didn't flag it as required

export type EvidenceFlag = "fur" | "leather" | "luxury" | "exotic";
export type EvidenceStatus = "provided" | "missing" | "optional";

export interface EvidenceShot {
  /** Short label for the shot, shown as the row title. */
  label: string;
  /** One-line "why this matters" — shown as the secondary line. */
  why: string;
  /** Lowercase keywords used to fuzzy-match against AI evidence/prompts. */
  keywords: string[];
}

export const EVIDENCE_CHECKLIST: Record<EvidenceFlag, EvidenceShot[]> = {
  fur: [
    {
      label: "Base of pelt",
      why: "Real fur grows from leather skin; faux from a woven backing. Single most reliable test.",
      keywords: ["pelt", "base of pelt", "follicle", "skin", "backing", "hide"],
    },
    {
      label: "Hair tip close-up",
      why: "Real fur tapers to a fine point; faux is blunt-cut.",
      keywords: ["hair tip", "tip of", "tapered", "blunt", "fiber tip"],
    },
    {
      label: "Against-the-grain shot",
      why: "Pushing the fur back exposes the underlying skin / backing.",
      keywords: ["against the grain", "roll back", "push back", "part the fur", "grain"],
    },
    {
      label: "Care or origin label",
      why: "Many vintage furs are unlabeled — but if there's any tag, it's evidence.",
      keywords: ["label", "tag", "care label", "origin", "made in"],
    },
  ],
  leather: [
    {
      label: "Cut edge / cross-section",
      why: "Real leather has a fibrous interior; faux is uniform foam or fabric.",
      keywords: ["edge", "cross-section", "cut", "interior", "fibrous", "section"],
    },
    {
      label: "Pore pattern macro",
      why: "Real leather pores are irregular; printed/faux pores repeat.",
      keywords: ["pore", "grain", "macro", "texture", "surface"],
    },
    {
      label: "Underside / suede backing",
      why: "Reverse side of real leather shows raw suede; faux shows fabric.",
      keywords: ["underside", "back", "suede", "reverse", "lining"],
    },
    {
      label: "Hardware backstamp",
      why: "On bags/jackets, branded zippers and rivets corroborate origin.",
      keywords: ["hardware", "zipper", "rivet", "snap", "backstamp", "stamp"],
    },
  ],
  luxury: [
    {
      label: "Date code / serial macro",
      why: "Most luxury houses stamp a date or serial inside. Macro reveals font fidelity.",
      keywords: ["date code", "serial", "stamp", "heat stamp", "code"],
    },
    {
      label: "Stitching close-up",
      why: "Stitches per inch and thread color are very specific to each brand.",
      keywords: ["stitch", "stitching", "thread", "seam", "spi"],
    },
    {
      label: "Hardware engraving",
      why: "Zipper pulls, lock plates, and feet are engraved on authentic pieces.",
      keywords: ["hardware", "engraving", "engraved", "zipper", "lock", "plate", "feet"],
    },
    {
      label: "Interior label + care tag",
      why: "One frame showing both proves provenance match.",
      keywords: ["interior label", "care tag", "label", "tag", "lining"],
    },
    {
      label: "Authentication card / dust bag",
      why: "Original paperwork raises tier substantially when present.",
      keywords: ["authenticity", "card", "dust bag", "paperwork", "receipt"],
    },
  ],
  exotic: [
    {
      label: "Scale pattern macro",
      why: "Real exotic skin has irregular scales; embossed leather repeats.",
      keywords: ["scale", "scales", "pattern", "macro", "texture"],
    },
    {
      label: "Edge / underside",
      why: "Real exotic has natural skin texture on the back; faux is uniform.",
      keywords: ["edge", "underside", "back", "reverse"],
    },
    {
      label: "CITES tag or paperwork",
      why: "Legally-imported exotics carry CITES paperwork. Photograph any tag.",
      keywords: ["cites", "paperwork", "permit", "tag", "documentation"],
    },
    {
      label: "Hardware / brand mark",
      why: "Brand stamping on exotic pieces is usually deliberate and crisp.",
      keywords: ["hardware", "stamp", "brand", "engraving"],
    },
  ],
};

/**
 * Resolve each canonical shot's status by matching its keywords against the
 * AI's free-text evidence (positive signal) and prompts (still needed).
 * Pure — safe to call from render.
 */
export function resolveChecklist(
  flag: EvidenceFlag,
  evidence: string[] = [],
  prompts: string[] = [],
): { shot: EvidenceShot; status: EvidenceStatus }[] {
  const evText = evidence.join(" \n ").toLowerCase();
  const prText = prompts.join(" \n ").toLowerCase();
  return EVIDENCE_CHECKLIST[flag].map((shot) => {
    const inEvidence = shot.keywords.some((k) => evText.includes(k));
    const inPrompts  = shot.keywords.some((k) => prText.includes(k));
    let status: EvidenceStatus;
    if (inEvidence) status = "provided";
    else if (inPrompts) status = "missing";
    else status = "optional";
    return { shot, status };
  });
}

export const FLAG_LABEL: Record<EvidenceFlag, string> = {
  fur: "Fur",
  leather: "Leather",
  luxury: "Luxury house",
  exotic: "Exotic skin",
};